import { queryOptions } from '@tanstack/react-query'

/**
 * Suspense-compatible queryOptions builder for the orval client.
 *
 * Orval's generated queryFn types include `skipToken` from TanStack Query 5,
 * which `useSuspenseQuery` rejects. Our `customFetch` (lib/api/fetcher.ts)
 * also returns the parsed JSON body directly, while orval's generated
 * response type pretends it's `{ data, status, headers }`.
 *
 * This wrapper produces a `queryOptions` object whose queryFn return type
 * matches the actual runtime shape (`T`), making it Suspense-compatible.
 *
 * Supports the most common options: `queryKey`, `queryFn`, `staleTime`,
 * `enabled`. For other TanStack Query options (`gcTime`, `retry`, `select`,
 * `refetchOnWindowFocus`), fall back to raw `queryOptions()` from
 * @tanstack/react-query — the wrapper deliberately keeps a minimal surface.
 */
type Input<T> = {
  queryKey: readonly unknown[]
  queryFn: (ctx: { signal?: AbortSignal }) => Promise<unknown>
  staleTime?: number
  enabled?: boolean
}

export function typedSuspenseOptions<T>(input: Input<T>) {
  return queryOptions({
    queryKey: input.queryKey,
    queryFn: input.queryFn as (ctx: { signal?: AbortSignal }) => Promise<T>,
    staleTime: input.staleTime,
    enabled: input.enabled,
  })
}
